import { useState } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { PageHeader } from "../components/AppLayout";
import { StatusBadge } from "../components/StatusBadge";
import { Download, Home, Wallet, Wrench, Building2, FileText, Send } from "lucide-react";
import { toast } from "sonner";
import {
  CONTRATOS,
  COBROS,
  RECLAMOS,
  liquidacionesDePropietario,
} from "../data/mockData";
import { pesos, fechaCorta } from "../lib/format";
import { generarLiquidacionPDF } from "../lib/liquidacionPdf";
import { useBrand } from "../context/BrandContext";

export default function Portales() {
  const [rol, setRol] = useState("inquilino");
  return (
    <div>
      <PageHeader
        titulo="Portal de inquilinos y propietarios"
        descripcion="Cada cliente ve lo suyo: el inquilino su contrato, próximo pago y reclamos; el propietario sus propiedades, cobros y liquidaciones."
      />
      <Tabs value={rol} onValueChange={setRol} className="mb-6">
        <TabsList className="rounded-md" data-testid="portal-role-switch">
          <TabsTrigger value="inquilino" data-testid="portal-tab-inquilino" className="rounded-md">Vista inquilino</TabsTrigger>
          <TabsTrigger value="propietario" data-testid="portal-tab-propietario" className="rounded-md">Vista propietario</TabsTrigger>
        </TabsList>
      </Tabs>
      {rol === "inquilino" ? <Inquilino /> : <Propietario />}
    </div>
  );
}

function Inquilino() {
  const contrato = CONTRATOS[0];
  const cobro = COBROS.find((c) => c.contratoId === contrato.id) || COBROS[0];
  const reclamos = RECLAMOS.filter((r) => r.contratoId === contrato.id);
  const [form, setForm] = useState({ titulo: "", desc: "" });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="p-5 rounded-lg border-slate-200 shadow-sm">
        <div className="flex items-center gap-2 mb-4"><Home className="h-4 w-4 text-slate-400" /><h3 className="text-base font-semibold text-slate-900">Mi contrato</h3></div>
        <div className="space-y-3 text-sm">
          <Info l="Dirección" v={contrato.direccion} />
          <Info l="Monto mensual" v={pesos(contrato.montoMensual)} />
          <Info l="Índice de ajuste" v={`${contrato.indice} · cada ${contrato.periodicidadAjusteMeses} meses`} />
          <Info l="Próximo ajuste" v={`${fechaCorta(contrato.proximaActualizacion)} → ${pesos(contrato.proximoMontoEstimado)}`} />
          <Info l="Vencimiento" v={fechaCorta(contrato.vencimiento)} />
        </div>
      </Card>

      <Card className="p-5 rounded-lg border-slate-200 shadow-sm">
        <div className="flex items-center gap-2 mb-4"><Wallet className="h-4 w-4 text-slate-400" /><h3 className="text-base font-semibold text-slate-900">Próximo pago</h3></div>
        <p className="text-3xl font-bold text-slate-900">{pesos(cobro.monto)}</p>
        <p className="text-sm text-slate-500 mb-1">Vence el {fechaCorta(cobro.vencimiento)}</p>
        <StatusBadge estado={cobro.estado} />
        <Button className="btn-brand rounded-md w-full mt-4" data-testid="portal-pay-button" onClick={() => toast.success("Pago registrado (demo).")}>Pagar ahora</Button>
        <Button variant="outline" className="rounded-md w-full mt-2" data-testid="portal-download-receipt" onClick={() => toast.success("Comprobante descargado (demo).")}>
          <Download className="mr-1.5 h-4 w-4" /> Descargar comprobante
        </Button>
      </Card>

      <Card className="p-5 rounded-lg border-slate-200 shadow-sm">
        <div className="flex items-center gap-2 mb-4"><Wrench className="h-4 w-4 text-slate-400" /><h3 className="text-base font-semibold text-slate-900">Reclamos</h3></div>
        <div className="space-y-2 mb-4">
          {reclamos.length === 0 && <p className="text-sm text-slate-400">No tenés reclamos abiertos.</p>}
          {reclamos.map((r) => (
            <div key={r.id} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <span className="text-sm text-slate-700">{r.titulo}</span>
              <StatusBadge estado={r.estado} />
            </div>
          ))}
        </div>
        <div className="space-y-2 pt-3 border-t border-slate-100">
          <Label className="text-xs">Nuevo reclamo</Label>
          <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Título del problema" className="rounded-md" data-testid="portal-ticket-title" />
          <Textarea value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} placeholder="Contanos qué pasó…" className="rounded-md" />
          <Button size="sm" className="btn-brand rounded-md w-full" data-testid="portal-submit-ticket-button"
            onClick={() => { setForm({ titulo: "", desc: "" }); toast.success("Reclamo enviado. Te vamos a avisar cuando se asigne."); }}>
            <Send className="mr-1.5 h-4 w-4" /> Enviar reclamo
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Propietario() {
  const { brand } = useBrand();
  const propietarios = [...new Set(CONTRATOS.map((c) => c.propietario.nombre))];
  const [sel, setSel] = useState(propietarios[0]);
  const sus = CONTRATOS.filter((c) => c.propietario.nombre === sel);
  const liqs = liquidacionesDePropietario(sel);
  const totalNeto = liqs.reduce((s, l) => s + l.neto, 0);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <p className="text-sm text-slate-600">Propietario:</p>
        <Select value={sel} onValueChange={setSel}>
          <SelectTrigger className="w-full sm:w-[280px] rounded-md" data-testid="portal-owner-select"><SelectValue /></SelectTrigger>
          <SelectContent>{propietarios.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <div className="sm:ml-auto rounded-md bg-emerald-50 border border-emerald-200 px-4 py-2">
          <span className="text-xs text-emerald-700">Neto a cobrar este mes: </span>
          <span className="text-sm font-bold text-emerald-800">{pesos(totalNeto)}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="rounded-lg border-slate-200 shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" /><h3 className="text-base font-semibold text-slate-900">Mis propiedades</h3></div>
          <div className="divide-y divide-slate-100">
            {sus.map((c) => {
              const cobro = COBROS.find((x) => x.contratoId === c.id);
              return (
                <div key={c.id} className="flex items-center justify-between px-5 py-3">
                  <div><p className="text-sm font-medium text-slate-900">{c.direccion}</p><p className="text-xs text-slate-500">Inquilino: {c.inquilino.nombre}</p></div>
                  <div className="text-right"><p className="text-sm font-medium">{pesos(c.montoMensual)}</p>{cobro && <StatusBadge estado={cobro.estado} />}</div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="rounded-lg border-slate-200 shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2"><FileText className="h-4 w-4 text-slate-400" /><h3 className="text-base font-semibold text-slate-900">Liquidaciones</h3></div>
          <div className="divide-y divide-slate-100">
            {liqs.map((l) => (
              <div key={l.id} className="flex items-center justify-between px-5 py-3">
                <div><p className="text-sm font-medium text-slate-900">{l.direccion}</p><p className="text-xs text-slate-500">Neto {pesos(l.neto)}</p></div>
                <Button size="sm" variant="outline" className="rounded-md"
                  data-testid={`portal-liquidacion-pdf-${l.contratoId}`}
                  onClick={() => { generarLiquidacionPDF({ agencia: brand.nombre, color: brand.color, liquidacion: l, periodo: "Mes en curso" }); toast.success("Liquidación descargada."); }}>
                  <Download className="mr-1.5 h-3.5 w-3.5" /> PDF
                </Button>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Info({ l, v }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500">{l}</span>
      <span className="text-slate-800 font-medium text-right">{v}</span>
    </div>
  );
}
